import React, { useState } from 'react';

import { useFeatureFlags } from 'src/context';

import { SIDEBAR_NOTIFICATION_CARD_HEIGHT } from './SidebarNotificationCard';

// Figma geometry, prototype motion: collapsed, the back card sits 5px lower
// and narrowed to 216/224 behind the front one; hovering fans them out 4px
// apart with the prototype's easing and stagger.
const PEEK_OFFSET = 5;
const COLLAPSED_INSET = (224 - 216) / 2;
const EXPANDED_GAP = 4;
const STAGGER_MS = 30;
const TRANSITION = 'cubic-bezier(0.32, 0.72, 0, 1) 260ms';
const FRONT_SHADOW = 'drop-shadow(0px 1px 0.35px rgba(236, 239, 246, 0.49))';

// The onboarding-activation prototype shrinks the back card instead of
// narrowing it, peeks it further and fans the deck out wider, with no shadow.
const LIGHT_PEEK_OFFSET = 7;
const LIGHT_BACK_SCALE = 0.955;
const LIGHT_EXPANDED_GAP = 8;

type StackCard = { key: string; node: React.ReactNode };

const stackHeight = (count: number, isExpanded: boolean) =>
  isExpanded
    ? count * SIDEBAR_NOTIFICATION_CARD_HEIGHT + (count - 1) * EXPANDED_GAP
    : SIDEBAR_NOTIFICATION_CARD_HEIGHT + (count - 1) * PEEK_OFFSET;

const lightStackHeight = (count: number, isExpanded: boolean) =>
  isExpanded
    ? count * SIDEBAR_NOTIFICATION_CARD_HEIGHT +
      (count - 1) * LIGHT_EXPANDED_GAP
    : SIDEBAR_NOTIFICATION_CARD_HEIGHT + (count - 1) * LIGHT_PEEK_OFFSET;

const cardStyle = (
  index: number,
  count: number,
  isExpanded: boolean,
): React.CSSProperties => {
  const isFront = index === 0;
  const offset = isExpanded
    ? index * (SIDEBAR_NOTIFICATION_CARD_HEIGHT + EXPANDED_GAP)
    : index * PEEK_OFFSET;
  // As in the prototype, the back card trails the fan-out by one step and
  // leads the fold-back.
  const delay = isExpanded === isFront ? 0 : STAGGER_MS;
  const inset = isExpanded || isFront ? 0 : COLLAPSED_INSET;
  return {
    transform: `translateY(${offset}px)`,
    left: inset,
    right: inset,
    zIndex: count - index,
    filter: !isExpanded && isFront && count > 1 ? FRONT_SHADOW : 'none',
    transition: ['transform', 'left', 'right', 'filter']
      .map((property) => `${property} ${TRANSITION} ${delay}ms`)
      .join(', '),
  };
};

const lightCardStyle = (
  index: number,
  count: number,
  isExpanded: boolean,
): React.CSSProperties => {
  const isFront = index === 0;
  const offset = isExpanded
    ? index * (SIDEBAR_NOTIFICATION_CARD_HEIGHT + LIGHT_EXPANDED_GAP)
    : index * LIGHT_PEEK_OFFSET;
  const delay = isExpanded === isFront ? 0 : STAGGER_MS;
  const scale = isExpanded || isFront ? 1 : LIGHT_BACK_SCALE;
  return {
    transform: `translateY(${offset}px) scale(${scale})`,
    transformOrigin: 'top center',
    left: 0,
    right: 0,
    zIndex: count - index,
    transition: `transform ${TRANSITION} ${delay}ms`,
  };
};

/** Sidebar notifications that fold into a deck and fan out on hover. */
export function SidebarNotificationStack({ cards }: { cards: StackCard[] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { EnableOnboardingActivation } = useFeatureFlags();

  if (!cards.length) {
    return null;
  }

  const getHeight = EnableOnboardingActivation ? lightStackHeight : stackHeight;
  const getCardStyle = EnableOnboardingActivation ? lightCardStyle : cardStyle;

  return (
    <div
      // Under onboarding-activation the stack sits 8px below the checklist
      // (this margin collapses with the checklist's 4px one), and flush with
      // the column's padding when nothing is above it.
      className={
        EnableOnboardingActivation
          ? 'group/notifications relative mb-3 not-first:mt-2'
          : 'group/notifications relative mb-3'
      }
      data-testid="sidebar-notification-stack"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsExpanded(false);
        }
      }}
      onFocus={() => setIsExpanded(true)}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      style={{
        height: getHeight(cards.length, isExpanded),
        transition: `height ${TRANSITION}`,
      }}
    >
      {cards.map(({ key, node }, index) => (
        <div
          className="absolute top-0"
          key={key}
          style={getCardStyle(index, cards.length, isExpanded)}
        >
          {node}
        </div>
      ))}
    </div>
  );
}
